import moment from "moment";
import { Button, Col, Row, Table, Tag, Collapse, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";

import nodataImgUrl from "@/assets/nodata.png";
import { FullLoading } from "@/components/FullLoading";
import { formatBalance } from "@/utils/formatters";
import sdk, { GraphQLError } from "@/sdk";

const { Panel } = Collapse;

interface WithdrawalRequest {
  _id: string;
  status: string;
  releasedCashAmount?: number | null;
  releasedHoldAmount?: number | null;
  createdAt: string;
  updatedAt: string;
  stocks?: Array<{
    stock: { symbol: string; localizedName?: { en?: string } };
    quantity: number;
    purchasedAt: string;
  }>;
  bonds?: Array<{
    bond: { name: string };
    amount: number;
    purchasedAt: string;
  }>;
  estate?: Array<{
    estate: { name: string; bankName?: string };
    amount: number;
    purchasedAt: string;
  }>;
  confirmationDetail?: Array<{
    stocks?: Array<{
      assetId: string;
      totalPrice: number;
      totalAmount: number;
      totalComission: number;
      totalFee: number;
    }>;
    bonds?: Array<{
      assetId: string;
      principleAmount: number;
      interestAmount: number;
      taxAmount: number;
      feeAmount: number;
      totalAmount: number;
    }>;
    estate?: Array<{
      assetId: string;
      principleAmount: number;
      interestAmount: number;
      taxAmount: number;
      feeAmount: number;
      totalAmount: number;
    }>;
  }>;
}

export const WithdrawalTab = ({ _id }: { _id: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["userWithdrawalRequests", _id],
    queryFn: () =>
      (sdk as any).userWithdrawalRequests({ userId: _id }) as Promise<{
        userWithdrawalRequests: WithdrawalRequest[];
      }>,
    select: (data) => data.userWithdrawalRequests,
  });

  const downloadMutation = useMutation({
    mutationFn: () =>
      (sdk as any).downloadUserWithdrawals({ userId: _id }) as Promise<{
        downloadUserWithdrawals: string;
      }>,
    onSuccess: (data) => {
      const url = data.downloadUserWithdrawals;
      window.open(url, "_blank");
      message.success("Excel файл амжилттай үүслээ");
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа"
      );
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Pending":
      case "Processing":
        return "orange";
      case "TransferPending":
        return "blue";
      case "Done":
        return "green";
      case "Canceled":
        return "red";
      default:
        return "default";
    }
  };

  // Backend нь confirmationDetail-ийн мөнгөн дүнгүүдийг бүхэл тоонд тохируулж
  // 100 дахин томруулж хадгалдаг (cent-тэй адил), тиймээс UI-д харуулахын
  // өмнө 100-д хуваана.
  const CONFIRMATION_AMOUNT_DIVISOR = 100;
  const fromConfirmation = (value: number | null | undefined): number | null =>
    typeof value === "number" && Number.isFinite(value)
      ? value / CONFIRMATION_AMOUNT_DIVISOR
      : null;

  const calculateTotal = (record: WithdrawalRequest) => {
    let total = 0;
    record.confirmationDetail?.forEach((detail) => {
      detail.stocks?.forEach((s) => (total += s.totalAmount));
      detail.bonds?.forEach((b) => (total += b.totalAmount));
      detail.estate?.forEach((e) => (total += e.totalAmount));
    });
    // Released cash isn't stored as decimal2; add it after the divisor.
    const cash =
      (record.releasedCashAmount ?? 0) + (record.releasedHoldAmount ?? 0);
    return total / CONFIRMATION_AMOUNT_DIVISOR + cash;
  };

  // Flatten and merge assets with their confirmation details by index
  const getMergedStocks = (record: WithdrawalRequest) => {
    const confirmations = record.confirmationDetail?.flatMap((d) => d.stocks ?? []) ?? [];
    return (record.stocks ?? []).map((stock, index) => ({
      ...stock,
      confirmation: confirmations[index] ?? null,
    }));
  };
  const getMergedBonds = (record: WithdrawalRequest) => {
    const confirmations = record.confirmationDetail?.flatMap((d) => d.bonds ?? []) ?? [];
    return (record.bonds ?? []).map((bond, index) => ({
      ...bond,
      confirmation: confirmations[index] ?? null,
    }));
  };
  const getMergedEstate = (record: WithdrawalRequest) => {
    const confirmations = record.confirmationDetail?.flatMap((d) => d.estate ?? []) ?? [];
    return (record.estate ?? []).map((estate, index) => ({
      ...estate,
      confirmation: confirmations[index] ?? null,
    }));
  };

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img width="250" src={nodataImgUrl} alt="no-data" />
        <p>Мэдээлэл олдсонгүй</p>
      </div>
    );
  }

  return (
    <div className="page-body">
      <Row justify="center">
        {isLoading ? (
          <FullLoading />
        ) : (
          <Col span={24}>
            <div style={{ marginBottom: 16 }}>
              <Button
                icon={<FileExcelOutlined />}
                loading={downloadMutation.isLoading}
                onClick={() => downloadMutation.mutate()}
              >
                Excel татах
              </Button>
            </div>
            <Table
              pagination={false}
              dataSource={data ?? []}
              loading={isLoading}
              size="small"
              bordered
              rowKey="_id"
              expandable={{
                expandedRowRender: (record) => (
                  <Collapse>
                    {record.stocks && record.stocks.length > 0 && (
                      <Panel header="Хувьцаа" key="stocks">
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={getMergedStocks(record)}
                          rowKey={(item) => item.stock.symbol}
                          columns={[
                            { title: "Симбол", dataIndex: ["stock", "symbol"] },
                            { title: "Нэр", dataIndex: ["stock", "localizedName", "en"] },
                            { title: "Тоо ширхэг", dataIndex: "quantity" },
                            {
                              title: "Худалдан авсан огноо",
                              dataIndex: "purchasedAt",
                              render: (text) => moment(text).format("YYYY/MM/DD"),
                            },
                            {
                              title: "Нийт үнэ",
                              dataIndex: ["confirmation", "totalPrice"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Нийт дүн",
                              dataIndex: ["confirmation", "totalAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Шимтгэл",
                              dataIndex: ["confirmation", "totalComission"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Хураамж",
                              dataIndex: ["confirmation", "totalFee"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                          ]}
                        />
                      </Panel>
                    )}
                    {record.bonds && record.bonds.length > 0 && (
                      <Panel header="Бонд" key="bonds">
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={getMergedBonds(record)}
                          rowKey={(item) => item.bond.name}
                          columns={[
                            { title: "Нэр", dataIndex: ["bond", "name"] },
                            {
                              title: "Дүн",
                              dataIndex: "amount",
                              render: (text) => <span>{formatBalance({ balance: text })} ₮</span>,
                            },
                            {
                              title: "Худалдан авсан огноо",
                              dataIndex: "purchasedAt",
                              render: (text) => moment(text).format("YYYY/MM/DD"),
                            },
                            {
                              title: "Үндсэн дүн",
                              dataIndex: ["confirmation", "principleAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Хүү",
                              dataIndex: ["confirmation", "interestAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Татвар",
                              dataIndex: ["confirmation", "taxAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Хураамж",
                              dataIndex: ["confirmation", "feeAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Нийт",
                              dataIndex: ["confirmation", "totalAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                          ]}
                        />
                      </Panel>
                    )}
                    {record.estate && record.estate.length > 0 && (
                      <Panel header="Үл хөдлөх" key="estate">
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={getMergedEstate(record)}
                          rowKey={(item) => item.estate.name}
                          columns={[
                            { title: "Нэр", dataIndex: ["estate", "name"] },
                            { title: "Банк", dataIndex: ["estate", "bankName"] },
                            {
                              title: "Дүн",
                              dataIndex: "amount",
                              render: (text) => <span>{formatBalance({ balance: text })} ₮</span>,
                            },
                            {
                              title: "Худалдан авсан огноо",
                              dataIndex: "purchasedAt",
                              render: (text) => moment(text).format("YYYY/MM/DD"),
                            },
                            {
                              title: "Үндсэн дүн",
                              dataIndex: ["confirmation", "principleAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Хүү",
                              dataIndex: ["confirmation", "interestAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Татвар",
                              dataIndex: ["confirmation", "taxAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Хураамж",
                              dataIndex: ["confirmation", "feeAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                            {
                              title: "Нийт",
                              dataIndex: ["confirmation", "totalAmount"],
                              render: (t) => {
                                const v = fromConfirmation(t);
                                return v !== null ? <span>{formatBalance({ balance: v })} ₮</span> : "-";
                              },
                            },
                          ]}
                        />
                      </Panel>
                    )}
                    {(record.releasedCashAmount || record.releasedHoldAmount) && (
                      <Panel header="Бэлэн мөнгө" key="cash">
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={[
                            ...(record.releasedCashAmount
                              ? [{ label: "Бэлэн мөнгө", amount: record.releasedCashAmount }]
                              : []),
                            ...(record.releasedHoldAmount
                              ? [{ label: "Худалдан авалтаас үлдсэн дүн", amount: record.releasedHoldAmount }]
                              : []),
                          ]}
                          rowKey="label"
                          columns={[
                            { title: "Төрөл", dataIndex: "label" },
                            {
                              title: "Дүн",
                              dataIndex: "amount",
                              render: (text) => (
                                <span>{formatBalance({ balance: text })} ₮</span>
                              ),
                            },
                          ]}
                        />
                      </Panel>
                    )}
                  </Collapse>
                ),
              }}
              columns={[
                {
                  title: "Огноо",
                  dataIndex: "createdAt",
                  key: "createdAt",
                  render: (text) => (
                    <span>{moment(text).format("YYYY/MM/DD HH:mm:ss")}</span>
                  ),
                },
                {
                  title: "Шинэчлэгдсэн",
                  dataIndex: "updatedAt",
                  key: "updatedAt",
                  render: (text) => (
                    <span>{moment(text).format("YYYY/MM/DD HH:mm:ss")}</span>
                  ),
                },
                {
                  title: "Төлөв",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => (
                    <Tag color={getStatusColor(status)}>{status}</Tag>
                  ),
                },
                {
                  title: "Хувьцаа",
                  key: "stocksCount",
                  render: (_, record) => (
                    <span>{record.stocks?.length ?? 0} ширхэг</span>
                  ),
                },
                {
                  title: "Бонд",
                  key: "bondsCount",
                  render: (_, record) => (
                    <span>{record.bonds?.length ?? 0} ширхэг</span>
                  ),
                },
                {
                  title: "Үл хөдлөх",
                  key: "estateCount",
                  render: (_, record) => (
                    <span>{record.estate?.length ?? 0} ширхэг</span>
                  ),
                },
                {
                  title: "Бэлэн мөнгө",
                  key: "cashAmount",
                  render: (_, record) => {
                    const total = (record.releasedCashAmount ?? 0) + (record.releasedHoldAmount ?? 0);
                    return total > 0 ? (
                      <span>{formatBalance({ balance: total })} ₮</span>
                    ) : (
                      <span style={{ color: "#888" }}>-</span>
                    );
                  },
                },
                {
                  title: "Нийт дүн",
                  key: "total",
                  render: (_, record) => {
                    const total = calculateTotal(record);
                    return total > 0 ? (
                      <span style={{ fontWeight: "bold" }}>
                        {formatBalance({ balance: total })} ₮
                      </span>
                    ) : (
                      <span style={{ color: "#888" }}>-</span>
                    );
                  },
                },
              ]}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};
